import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import { eventSchema, eventDefaults } from "../../../../models/event";
import { 
    NeoCard, 
    NeoButton, 
    NeoInput, 
    NeoBadge, 
    NeoAlert, 
    NeoEmptyState, 
    OverlayLoader 
} from "../../../../components/Common";
import { EVENT_TYPES, TEST_TYPES, getEventStatus } from "../constants";
import { GLOBAL_STATUS } from "../../../../constants/common";
import { GLOBAL_STATUS_LABELS } from "../../../../constants/common";
import EventReportModal from "./EventReportModal";
import TestReportModal from "./TestReportModal";
import ExtraEventReportModal from "./ExtraEventReportModal";
import { useTenant } from "../../../../hooks/useTenant";
import { useAuthorization } from "../../../../hooks/useAuthorization";
import { storage } from "../../../../services/firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

/**
 * EventEditor
 * Formulário principal de Eventos.
 * Migrado para o Neo Design System (100% Neo components e zero estilos inline).
 */
const EventEditor = ({ event, onSave, onDelete, isSaving, onChange }) => {

    const [isReportOpen, setIsReportOpen] = useState(false);
    const [isUploadingBadge, setIsUploadingBadge] = useState(false);
    const { idTenant } = useTenant();
    const { hasFeature } = useAuthorization();

    const handleBadgeUpload = useCallback(async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setIsUploadingBadge(true);
        try {
            const storageRef = ref(storage, `tenants/${idTenant}/events/badges/${Date.now()}_${file.name}`);
            await uploadBytes(storageRef, file);
            const url = await getDownloadURL(storageRef);
            formik.setFieldValue("badgeImageUrl", url);
        } catch (err) {
            console.error("Erro ao fazer upload da insígnia:", err);
        } finally {
            setIsUploadingBadge(false);
        }
    }, [idTenant]); // eslint-disable-line react-hooks/exhaustive-deps

    const formik = useFormik({
        initialValues: eventDefaults,
        validationSchema: eventSchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            onSave(values);
        },
    });

    // Sincroniza estado
    useEffect(() => {
        if (event) {
            formik.resetForm({
                values: {
                    ...eventDefaults,
                    ...event,
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [event]);

    useEffect(() => {
        onChange(formik.dirty);
    }, [formik.dirty, onChange]);

    if (!event) {
        return (
            <NeoEmptyState
                icon="mdi-calendar-star"
                title="Selecione um evento"
                description="Escolha um evento na lista ao lado ou cadastre um novo para avaliações e testes."
            />
        );
    }

    const availableEvents = EVENT_TYPES.map(t => {
        let disabled = false;
        let warning = "";
        if (t.value === 'test' && !hasFeature('performance_module')) {
            disabled = true;
            warning = " (Upgrade Necessário)";
        }
        if (t.value === 'extra' && !hasFeature('gamification')) {
            disabled = true;
            warning = " (Upgrade Necessário)";
        }
        return { ...t, label: t.label + warning, disabled };
    });

    const isTestBlocked = formik.values.type === 'test' && !hasFeature('performance_module');
    const isExtraBlocked = formik.values.type === 'extra' && !hasFeature('gamification');
    const isSaveDisabled = isSaving || isTestBlocked || isExtraBlocked;

    return (
        <NeoCard
            title={formik.values.id ? formik.values.name : "Novo Evento"}
            subtitle={
                <span className="d-flex align-items-center gap-2 mt-1">
                    <span>Dados completos do evento</span>
                    {formik.values.id && (
                        <NeoBadge color={getEventStatus(formik.values) === GLOBAL_STATUS.ACTIVE ? 'success' : 'danger'}>
                            {GLOBAL_STATUS_LABELS[getEventStatus(formik.values)] || 'Desconhecido'}
                        </NeoBadge>
                    )}
                </span>
            }
            headerIcon="mdi-star-circle-outline"
            headerActions={
                <div className="d-flex gap-2">
                    {formik.values.id && (
                        <>
                            <NeoButton
                                variant="outline-danger"
                                square={true}
                                size="md"
                                icon="mdi-trash-can-outline"
                                onClick={() => onDelete(formik.values.id, formik.values.name)}
                                title="Excluir Evento"
                            />
                            <NeoButton
                                variant="outline-chocolate"
                                square={true}
                                size="md"
                                icon="mdi-printer"
                                onClick={() => setIsReportOpen(true)}
                                title="Imprimir Relatório"
                            />
                        </>
                    )}
                    <NeoButton
                        variant="primary"
                        loading={isSaving}
                        loadingText="Salvando..."
                        onClick={formik.handleSubmit}
                        disabled={isSaveDisabled}
                        icon="mdi-check"
                    >
                        Salvar
                    </NeoButton>
                </div>
            }
        >
            <OverlayLoader show={isSaving} label="Salvando evento..." />
            
            {(isTestBlocked || isExtraBlocked) && (
                <NeoAlert variant="warning" className="mb-4">
                    Seu plano atual não permite a criação de eventos deste tipo. Faça upgrade para desbloquear.
                </NeoAlert>
            )}

            <div className="row mb-4">
                <div className="col-12">
                    <h6 className="text-uppercase font-size-12 fw-bold text-muted mb-4 d-flex align-items-center">
                        <i className="mdi mdi-information-outline me-2 font-size-16 text-primary"></i>
                        Informações Básicas
                    </h6>
                </div>

                <div className="col-12 mb-3">
                    <NeoInput
                        label="Nome do Evento"
                        name="name"
                        type="text"
                        placeholder="Ex: Avaliação Física Mensal"
                        {...formik.getFieldProps("name")}
                        error={formik.touched.name && formik.errors.name}
                    />
                </div>

                <div className="col-md-6 mb-3">
                    <NeoInput
                        label="Tipo do Evento"
                        name="type"
                        type="select"
                        {...formik.getFieldProps("type")}
                        error={formik.touched.type && formik.errors.type}
                    >
                        {availableEvents.map(t => (
                            <option key={t.value} value={t.value} disabled={t.disabled}>{t.label}</option>
                        ))}
                    </NeoInput>
                </div>

                <div className="col-md-6 mb-3">
                    <NeoInput
                        label="Data de Início"
                        name="startDate"
                        type="date"
                        {...formik.getFieldProps("startDate")}
                        error={formik.touched.startDate && formik.errors.startDate}
                    />
                </div>

                <div className="col-md-6 mb-3">
                    <NeoInput
                        label="Data de Fim"
                        name="endDate"
                        type="date"
                        {...formik.getFieldProps("endDate")}
                        error={formik.touched.endDate && formik.errors.endDate}
                    />
                </div>
            </div>

            {formik.values.type === 'test' && (
                <React.Fragment>
                    <hr className="my-4 border-dashed" />
                    <div className="row">
                        <div className="col-12">
                            <h6 className="text-uppercase font-size-12 fw-bold text-muted mb-4 d-flex align-items-center">
                                <i className="mdi mdi-arm-flex-outline me-2 font-size-16 text-primary"></i>
                                Configurações do Teste
                            </h6>
                        </div>

                        <div className="col-md-4 mb-4">
                            <NeoInput
                                label="Formato do Teste"
                                name="testType"
                                type="select"
                                placeholder="Selecione o formato"
                                {...formik.getFieldProps("testType")}
                                error={formik.touched.testType && formik.errors.testType}
                                options={TEST_TYPES.map(t => ({ value: t.value, label: t.label }))}
                            />
                        </div>

                        {formik.values.testType === 'fixed_time' && (
                            <div className="col-md-4 mb-4">
                                <NeoInput
                                    label="Tempo Fixo (minutos)"
                                    name="fixedTime"
                                    type="number"
                                    placeholder="Ex: 12"
                                    {...formik.getFieldProps("fixedTime")}
                                    error={formik.touched.fixedTime && formik.errors.fixedTime}
                                />
                            </div>
                        )}

                        {formik.values.testType === 'fixed_distance' && (
                            <div className="col-md-4 mb-4">
                                <NeoInput
                                    label="Distância Fixa (metros)"
                                    name="fixedDistance"
                                    type="number"
                                    placeholder="Ex: 50"
                                    {...formik.getFieldProps("fixedDistance")}
                                    error={formik.touched.fixedDistance && formik.errors.fixedDistance}
                                />
                            </div>
                        )}
                    </div>
                </React.Fragment>
            )}

            {formik.values.type === 'extra' && (
                <React.Fragment>
                    <hr className="my-4 border-dashed" />
                    <div className="row">
                        <div className="col-12">
                            <h6 className="text-uppercase font-size-12 fw-bold text-muted mb-4 d-flex align-items-center">
                                <i className="mdi mdi-image-outline me-2 font-size-16 text-primary"></i>
                                Insígnia do Evento
                            </h6>
                        </div>
                        <div className="col-md-6 mb-4">
                            <div className="d-flex align-items-start gap-3">
                                <div className="avatar-lg rounded border border-chocolate bg-light d-flex align-items-center justify-content-center flex-shrink-0 overflow-hidden shadow-sm">
                                    {formik.values.badgeImageUrl ? (
                                        <img src={formik.values.badgeImageUrl} alt="Insígnia" className="w-100 h-100 object-fit-cover" />
                                    ) : (
                                        <i className="mdi mdi-medal-outline text-primary font-size-28"></i>
                                    )}
                                </div>
                                <div className="d-flex flex-column gap-2">
                                    <label className="neo-input-label mb-0">Imagem da Insígnia</label>
                                    <small className="text-muted">Esta imagem será exibida como conquista no perfil do aluno.</small>
                                    <div className="d-flex gap-2">
                                        <label className="btn btn-sm btn-outline-chocolate mb-0 cursor-pointer">
                                            <i className="mdi mdi-upload me-1"></i>
                                            {isUploadingBadge ? 'Enviando...' : 'Upload'}
                                            <input type="file" accept="image/*" className="d-none" onChange={handleBadgeUpload} disabled={isUploadingBadge} />
                                        </label>
                                        {formik.values.badgeImageUrl && (
                                            <NeoButton 
                                                variant="outline-danger" 
                                                size="sm" 
                                                square={true} 
                                                icon="mdi-close" 
                                                onClick={() => formik.setFieldValue('badgeImageUrl', '')} 
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6 mb-4">
                            <NeoInput
                                label="Recompensa de XP"
                                name="rewardXp"
                                type="number"
                                placeholder="Ex: 50"
                                {...formik.getFieldProps("rewardXp")}
                                error={formik.touched.rewardXp && formik.errors.rewardXp}
                            />
                            <small className="text-muted mt-2 d-block">
                                Pontos de experiência ganhos pelo aluno ao receber esta insígnia.
                            </small>
                        </div>
                    </div>
                </React.Fragment>
            )}

            <hr className="my-4 border-dashed" />

            <div className="row">
                <div className="col-12">
                    <h6 className="text-uppercase font-size-12 fw-bold text-muted mb-4 d-flex align-items-center">
                        <i className="mdi mdi-text-box-outline me-2 font-size-16 text-primary"></i>
                        Detalhes Adicionais
                    </h6>
                </div>

                <div className="col-12 mb-4">
                    <NeoInput
                        label="Descrição"
                        name="description"
                        type="textarea"
                        rows="3"
                        placeholder="Descrição sobre o evento..."
                        {...formik.getFieldProps("description")}
                        error={formik.touched.description && formik.errors.description}
                    />
                </div>
            </div>

            {formik.values.type === 'test' ? (
                <TestReportModal 
                    isOpen={isReportOpen} 
                    toggle={() => setIsReportOpen(!isReportOpen)} 
                    event={formik.values} 
                />
            ) : formik.values.type === 'extra' ? (
                <ExtraEventReportModal 
                    isOpen={isReportOpen} 
                    toggle={() => setIsReportOpen(!isReportOpen)} 
                    event={formik.values} 
                />
            ) : (
                <EventReportModal 
                    isOpen={isReportOpen} 
                    toggle={() => setIsReportOpen(!isReportOpen)} 
                    event={formik.values} 
                />
            )}
        </NeoCard>
    );
};

EventEditor.propTypes = {
    event: PropTypes.object,
    onSave: PropTypes.func,
    onDelete: PropTypes.func,
    isSaving: PropTypes.bool,
    onChange: PropTypes.func
};

export default EventEditor;
